/**
 * Toolbar Plugin for Lexical Editor
 * Based on Lexical Playground toolbar patterns
 */
import { $patchStyleText, $getSelectionStyleValueForProperty } from '@lexical/selection';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
    $getSelection,
    $isRangeSelection,
    CAN_REDO_COMMAND,
    CAN_UNDO_COMMAND,
    // COMMAND_PRIORITY_LOW,
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    REDO_COMMAND,
    SELECTION_CHANGE_COMMAND,
    UNDO_COMMAND,
} from 'lexical';
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { useCallback, useEffect, useRef, useState } from 'react';

const LowPriority = 1;

function useSafeLexicalComposerContext() {
    try {
        return useLexicalComposerContext();
    } catch (e) {
        console.warn('LexicalComposerContext not found in ToolbarPlugin:', e);
        return [null];
    }
}

function Divider() {
    return <div className="divider" />;
}

export default function ToolbarPlugin() {
    const [editor] = useSafeLexicalComposerContext();
    const toolbarRef = useRef(null);
    const [canUndo, setCanUndo] = useState(false);
    const [canRedo, setCanRedo] = useState(false);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [fontSize, setFontSize] = useState('15');

    const updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Update text format
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));

            // Update font size - keep in px
            const sizePx = $getSelectionStyleValueForProperty(selection, 'font-size', '15px');
            const pxVal = parseInt(sizePx);
            setFontSize(pxVal.toString());
        }
    }, []);

    useEffect(() => {
        if (!editor) {
            return undefined;
        }

        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    updateToolbar();
                    return false;
                },
                LowPriority,
            ),
            editor.registerCommand(
                CAN_UNDO_COMMAND,
                (payload) => {
                    setCanUndo(payload);
                    return false;
                },
                LowPriority,
            ),
            editor.registerCommand(
                CAN_REDO_COMMAND,
                (payload) => {
                    setCanRedo(payload);
                    return false;
                },
                LowPriority,
            ),
        );
    }, [editor, updateToolbar]);

    const formatBulletList = () => {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    };

    const formatNumberedList = () => {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    };

    const updateFontSize = (newSizePx) => {
        editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
                $patchStyleText(selection, { 'font-size': `${newSizePx}px` });
            }
        });
    };

    const onFontSizeChange = (e) => {
        const value = e.target.value;
        setFontSize(value);
    };

    const onFontSizeBlur = () => {
        const numValue = parseInt(fontSize);
        if (!isNaN(numValue) && numValue >= 8 && numValue <= 96) {
            updateFontSize(numValue);
        }
    };

    const incrementFontSize = () => {
        const current = parseInt(fontSize) || 15;
        const newSize = Math.min(current + 1, 96);
        setFontSize(newSize.toString());
        updateFontSize(newSize);
    };

    const decrementFontSize = () => {
        const current = parseInt(fontSize) || 15;
        const newSize = Math.max(current - 1, 8);
        setFontSize(newSize.toString());
        updateFontSize(newSize);
    };

    if (!editor) {
        return null;
    }

    return (
        <div className="toolbar" ref={toolbarRef}>
            <button
                type="button"
                disabled={!canUndo}
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(UNDO_COMMAND, undefined);
                }}
                className="toolbar-item spaced"
                aria-label="Undo"
                title="Undo (Ctrl+Z)"
            >
                <i className="format undo" />
            </button>
            <button
                type="button"
                disabled={!canRedo}
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(REDO_COMMAND, undefined);
                }}
                className="toolbar-item"
                aria-label="Redo"
                title="Redo (Ctrl+Y)"
            >
                <i className="format redo" />
            </button>
            <Divider />
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                }}
                className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
                aria-label="Format Bold"
                title="Bold (Ctrl+B)"
            >
                <i className="format bold" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                }}
                className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
                aria-label="Format Italics"
                title="Italic (Ctrl+I)"
            >
                <i className="format italic" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                }}
                className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
                aria-label="Format Underline"
                title="Underline (Ctrl+U)"
            >
                <i className="format underline" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                }}
                className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
                aria-label="Format Strikethrough"
                title="Strikethrough"
            >
                <i className="format strikethrough" />
            </button>
            <Divider />
            <div className="font-size-wrapper">
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        decrementFontSize();
                    }}
                    className="toolbar-item"
                    aria-label="Decrease font size"
                    title="Decrease font size"
                >
                    <i className="format minus" />
                </button>
                <input
                    type="number"
                    className="font-size-input"
                    value={fontSize}
                    onChange={onFontSizeChange}
                    onBlur={onFontSizeBlur}
                    min="8"
                    max="96"
                    title="Font size in pixels"
                />
                <button
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        incrementFontSize();
                    }}
                    className="toolbar-item"
                    aria-label="Increase font size"
                    title="Increase font size"
                >
                    <i className="format plus" />
                </button>
            </div>
            <Divider />
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    formatBulletList();
                }}
                className="toolbar-item spaced"
                aria-label="Bullet List"
                title="Bullet List"
            >
                <i className="format bullet-list" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    formatNumberedList();
                }}
                className="toolbar-item spaced"
                aria-label="Numbered List"
                title="Numbered List"
            >
                <i className="format numbered-list" />
            </button>
            <Divider />
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                }}
                className="toolbar-item spaced"
                aria-label="Left Align"
                title="Left Align"
            >
                <i className="format left-align" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
                }}
                className="toolbar-item spaced"
                aria-label="Center Align"
                title="Center Align"
            >
                <i className="format center-align" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
                }}
                className="toolbar-item spaced"
                aria-label="Right Align"
                title="Right Align"
            >
                <i className="format right-align" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault();
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
                }}
                className="toolbar-item"
                aria-label="Justify Align"
                title="Justify Align"
            >
                <i className="format justify-align" />
            </button>
        </div>
    );
}
